"use client";

/**
 * MagneticButton — Emil-style cursor pull effect.
 *
 * Wraps any clickable element and applies a subtle translation toward the
 * cursor position when hovered. Uses hardware-accelerated transform via
 * useMotionTemplate (NOT motion.div style={x,y} shorthand — that's CPU per
 * Emil's note).
 *
 * Strength 0.2-0.4 = subtle. >0.5 = too aggressive, feels broken.
 */
import { useRef } from "react";
import { motion, useMotionTemplate, useMotionValue, useSpring, type SpringOptions } from "motion/react";
const SPRING: SpringOptions = {
  stiffness: 150,
  damping: 18,
  mass: 0.4
};
export function MagneticButton({
  children,
  strength = 0.3,
  className = ""
}: {
  children: React.ReactNode;
  strength?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, SPRING);
  const sy = useSpring(y, SPRING);
  // Full transform string → off-main-thread compositor
  const transform = useMotionTemplate`translate3d(${sx}px, ${sy}px, 0)`;
  function onMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    x.set((e.clientX - cx) * strength);
    y.set((e.clientY - cy) * strength);
  }
  function onLeave() {
    x.set(0);
    y.set(0);
  }
  return <motion.div ref={ref} onPointerMove={onMove} onPointerLeave={onLeave} style={{
    transform,
    willChange: "transform"
  }} className={`inline-block motion-reduce:transform-none ${className}`}>
      {children}
    </motion.div>;
}